<script setup lang="ts">
const steps = [
  {icon: 'core-clock-watch', text: 'Регистрируйтесь\nза одну минуту'},
  {icon: 'core-bookmark-favorite-search', text: 'Находите\nлучшие предложения'},
  {icon: 'core-document-bell', text: 'Размещайте\nпартнёрские офферы'},
]
const props = withDefaults(defineProps<{ isDivider?: boolean }>(), {isDivider: false})

// Для ступенчатой анимации h1 - разбиваем на строки (построчно)
const h1Text = 'Каталог офферов\nи партнерских\nпрограмм'
const h1Lines = computed(() => {
  const lines = h1Text.split('\n')
  return lines.map((line, index) => ({
    text: index < lines.length - 1 ? `${line} ` : line,
    index: index
  }))
})
const h1Label = computed(() => h1Text.replace(/\s*\n\s*/g, ' '))

// Для анимации печатания h2 - разбиваем на символы (сохраняем пробелы)
const h2Text = 'Найдите лучшие офферы\nдля монетизации вашего трафика'
const h2Label = computed(() => h2Text.replace(/\s+/g, ' '))
const h2Chars = computed(() => {
  let index = 0
  return h2Text.split('').map(char => {
    if (char === '\n') {
      return { char: '\n', index: -1, isNewline: true } // перенос строки без индекса
    }
    const result = { char, index: index++, isNewline: false }
    return result
  })
})

// Вычисляем задержки
const h1LineDelay = 200
const h1AnimationDuration = 400
const h1EndTime = computed(() => {
  return (h1Lines.value.length - 1) * h1LineDelay + h1AnimationDuration
})
const h2CharsCount = computed(() => h2Chars.value.filter(c => !c.isNewline).length)
const h2AnimationDuration = 100
const h2CharDelay = computed(() => {
  if (h2CharsCount.value <= 1) return 0
  return Math.max(15, Math.floor((h1EndTime.value - h2AnimationDuration) / (h2CharsCount.value - 1)))
})
const h2CharDelayMs = computed(() => h2CharDelay.value + 'ms')
const h2AnimationDurationMs = computed(() => h2AnimationDuration + 'ms')

// Анимация benefits
const benefitAnimationDuration = 400
const benefitAnimationDurationMs = computed(() => benefitAnimationDuration + 'ms')
const benefitStepDelay = 400 // интервал между появлением элементов (400ms)
const benefitStepDelayMs = computed(() => benefitStepDelay + 'ms')

// Добавляем класс active при монтировании (минимальная задержка для корректной работы transition h1)
const isActive = ref(false)
onMounted(() => {
  isActive.value = true
})
</script>

<template>
  <section class="offer-exchange">
    <div class="offer-exchange-content">
      <h1 class="offer-exchange-header" :class="{ active: isActive }" :aria-label="h1Label">
        <span v-for="(line, i) in h1Lines"
              :key="i"
              class="header-line"
              aria-hidden="true"
              :style="{ '--index': line.index }">
          {{ line.text }}
        </span>
      </h1>
      <h2 class="offer-exchange-text"
          :class="{ active: isActive }"
          :aria-label="h2Label"
          :style="{ '--h2-char-delay': h2CharDelayMs }">
        <template v-for="(item, i) in h2Chars" :key="i">
          <span v-if="!item.isNewline" aria-hidden="true" :style="{ '--index': item.index }">{{ item.char }}</span>
          <br v-else aria-hidden="true"/>
        </template>
      </h2>
      <auth-layout-advertisement-cursor/>
    </div>
    <div v-if="isDivider" class="divider">
      <div class="divider-circle"/>
    </div>
    <div v-else
         class="offer-exchange-benefits"
         :style="{ '--benefit-step-delay': benefitStepDelayMs, '--benefit-animation-duration': benefitAnimationDurationMs }">
      <auth-layout-advertisement-benefits :is-active="isActive"/>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use 'assets/css/tokens/colors' as *;
@use 'assets/css/tokens/typography' as *;
@use 'sass:color';

/* Локальные переменные под scoped */
.offer-exchange {
  /* типографика */
  --h1: clamp(2.375rem, 4.8vw, 3.5rem);
  --h1-line: clamp(2.625rem, 4.2vw, 3.3125rem);
  --p: clamp(1.125rem, 2.2vw, 1.5rem);
  --p-line: clamp(1.5rem, 2.5vw, 1.875rem);

  /* размеры svg (сохранение пропорций по viewBox) */
  --art-w: clamp(5rem, 10vw, 9.5rem); /* 80 → 152 */

  /* ФЛЮИДНОЕ СМЕЩЕНИЕ:
     чем шире экран — тем левее и чуть ниже уезжает.
     Значения подбери на глаз под макет. */
  --art-right: clamp(-47px, calc(-60.36px + 0.03094 * 100vw), -29px);
  @media (min-width: 1024px) {
    /* десктопы: резко уходим к -143 и держим */
    --art-right: clamp(-143px, calc(145.33px - 0.178125 * 100vw), -29px);
  }
  --art-top: clamp(181px, calc(179.216px + 0.46453vw), 192px);

  display: flex;
  flex-direction: column;

  @media (max-width: 959px) {
    width: 100%;
  }
}

.offer-exchange-content {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: fit-content;
}

.offer-exchange-header {
  font-size: var(--h1);
  line-height: var(--h1-line);
  font-weight: $font-weight-semibold;
  letter-spacing: -4%;
  white-space: nowrap;
  display: block;

  // Начальное состояние для ступенчатой анимации (построчно)
  .header-line {
    display: block;
    opacity: 0;
    transform: translateY(-0.25rem);
    transition-duration: 400ms;
    transition-delay: calc(var(--index) * 200ms);
    transition-property: opacity, transform;
  }

  // Активное состояние
  &.active .header-line {
    opacity: 1;
    transform: translateY(0);
  }
}

.offer-exchange-text {
  font-size: var(--p);
  line-height: var(--p-line);
  font-weight: $font-weight-regular;
  color: $color-dark-grey;
  position: relative;
  white-space: pre-wrap; // сохраняем пробелы и переносы

  // Начальное состояние для анимации печатания
  span {
    opacity: 0;
    display: inline-block;
    animation: none; // Анимация не запускается до применения класса active
    // Сохраняем пробелы
    white-space: pre;
  }

  &.active span {
    // Запускаем анимацию только после применения класса active
    animation: type-in v-bind(h2AnimationDurationMs) forwards;
    animation-delay: calc(var(--index) * var(--h2-char-delay, 30ms));
  }

  @keyframes type-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
}

.divider {
  position: relative;
  height: 1px;
  width: 100%;
  margin-top: clamp(3.25rem, 8vw, 6.25rem);
  background-color: color.scale($color-divider, $lightness: 60%);

  .divider-circle{
    background-color: $color-dark;
    position: absolute;
    left: 50%;
    height: 0.5rem;
    width: 0.5rem;
    border-radius: 50%;
    top: -3px;
  }
  @media (min-width: 960px) {
    display: none;
  }
}

.offer-exchange-benefits {
  margin-top: 2rem;

  @media (max-width: 959px) {
    margin-top: 1.5rem;
  }
}
</style>
